import React from 'react';
import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import { GENDER } from 'domain/entities/CompanyUser/CompanyUser';
import { FormInput } from 'ui/components/elements/FormInput/FormInput';
import { FormSelect } from 'ui/components/elements/FormSelect/FormSelect';
import { GroupedToggleFormSelect } from 'ui/components/elements/FormSelect/GroupedToggleFormSelect';
import { FormError } from 'ui/components/elements/FormError/FormError';
import { FormLabel } from 'ui/components/elements/FormLabel/FormLabel';
import { ActionButton } from 'ui/components/elements/ActionButton/ActionButton';
import { SnackBar } from 'ui/components/elements/SnackBar/SnackBar';
import { ImageUpload } from 'ui/components/features/ImageUpload/ImageUpload';
import { useCompanyUserProfileRegistration } from './useCompanyUserProfileRegistration';
import { mqMin } from 'utils/responsive';

const BgWrapper = styled.div`
    background-color: #f8f9fd;
    height: 100%;
    padding: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow-y: auto;
`;

const Wrapper = styled.div`
    margin: 0 auto;
    padding: 30px;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
    font-size: var(--font-medium);
    text-align: center;

    ${mqMin('NARROW')} {
        width: 600px;
    }
`;

const TitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: center;
    margin-bottom: 20px;
`;

const Title = styled.div`
    font-size: var(--font-xx-large);
    color: var(--main-color-blue);
    font-weight: 600;
    text-align: center;
`;

const Step = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--main-color-blue);
    color: var(--white);
    font-size: var(--font-medium);
    font-weight: 700;
    border-radius: 8px;
    padding: 6px 15px;
    width: fit-content;
`;

const ButtonContainer = styled.div`
    margin: 10px auto;
    padding-bottom: 40px;
`;

const Birthday = styled.div`
    display: flex;
    width: 70%;
    > * {
        flex: 1;
    }

    @media (max-width: 768px) {
        width: 100%;
        > * {
            max-width: 33%;
        }
    }
`;

const Description = styled.div`
    font-size: var(--font-medium);
    text-align: left;
    font-weight: 600;
`;

export const CompanyUserProfileRegistration: React.FC = () => {
    const {
        control,
        loading,
        errors,
        showSnackbar,
        yearOptions,
        monthOptions,
        dayOptions,
        notCompletedProfile,
        setShowSnackbar,
        submit,
        watch,
        setValue,
    } = useCompanyUserProfileRegistration();

    return (
        <BgWrapper>
            <Wrapper>
                <SnackBar
                    message="ユーザープロフィールを設定しました。"
                    open={showSnackbar}
                    setOpen={setShowSnackbar}
                />
                <TitleContainer>
                    <Step>STEP2</Step>
                    <Title>担当者様のプロフィールを入力してください</Title>
                </TitleContainer>
                {notCompletedProfile && (
                    <Description>※サービスのご利用を開始するにはプロフィールの登録が必要です</Description>
                )}
                <FormLabel title="お名前（姓）" isRequired />
                <Controller
                    name="last_name"
                    control={control}
                    render={({ field }) => (
                        <div>
                            <FormInput {...field} hasError={!!errors.last_name?.message} />
                            {errors.last_name?.message && <FormError message={errors.last_name.message} />}
                        </div>
                    )}
                />

                <FormLabel title="お名前（名）" isRequired />
                <Controller
                    name="first_name"
                    control={control}
                    render={({ field }) => (
                        <div>
                            <FormInput {...field} hasError={!!errors.first_name?.message} />
                            {errors.first_name?.message && <FormError message={errors.first_name.message} />}
                        </div>
                    )}
                />

                <FormLabel title="ふりがな（姓）" isRequired />
                <Controller
                    name="last_name_kana"
                    control={control}
                    render={({ field }) => (
                        <div>
                            <FormInput {...field} hasError={!!errors.last_name_kana?.message} />
                            {errors.last_name_kana?.message && <FormError message={errors.last_name_kana.message} />}
                        </div>
                    )}
                />

                <FormLabel title="ふりがな（名）" isRequired />
                <Controller
                    name="first_name_kana"
                    control={control}
                    render={({ field }) => (
                        <div>
                            <FormInput {...field} hasError={!!errors.first_name_kana?.message} />
                            {errors.first_name_kana?.message && <FormError message={errors.first_name_kana.message} />}
                        </div>
                    )}
                />

                <FormLabel title="性別" isRequired />
                <Controller
                    name="gender"
                    control={control}
                    render={({ field }) => (
                        <div>
                            <FormSelect
                                {...field}
                                options={Object.entries(GENDER).map(([key, value]) => ({
                                    value: key,
                                    label: value,
                                }))}
                            />
                        </div>
                    )}
                />

                <FormLabel title="電話番号" isRequired />
                <Controller
                    name="phone_number"
                    control={control}
                    render={({ field }) => (
                        <div>
                            <FormInput {...field} value={field.value ?? ''} hasError={!!errors.phone_number?.message} />
                            {errors.phone_number?.message && <FormError message={errors.phone_number.message} />}
                        </div>
                    )}
                />

                <FormLabel title="生年月日" />
                <Birthday>
                    <Controller
                        name="year"
                        control={control}
                        render={({ field }) => (
                            <GroupedToggleFormSelect
                                {...field}
                                options={yearOptions}
                                placeholder="年"
                                name="年を選択"
                                error={errors.year?.message || errors.month?.message || errors.day?.message}
                                position="first"
                            />
                        )}
                    />
                    <Controller
                        name="month"
                        control={control}
                        render={({ field }) => (
                            <GroupedToggleFormSelect
                                {...field}
                                options={monthOptions}
                                placeholder="月"
                                name="月を選択"
                                position="middle"
                            />
                        )}
                    />
                    <Controller
                        name="day"
                        control={control}
                        render={({ field }) => (
                            <GroupedToggleFormSelect
                                {...field}
                                options={dayOptions}
                                placeholder="日"
                                name="日を選択"
                                position="last"
                            />
                        )}
                    />
                </Birthday>

                <FormLabel title="アイコン画像" />
                <ImageUpload
                    imagePath={watch('icon_image') ?? ''}
                    directory="company_profile"
                    fileName={'icon'}
                    fileCategory="company"
                    disabled={loading}
                    width={180}
                    height={180}
                    onComplete={(fileUrl) => setValue('icon_image', fileUrl)}
                />

                <FormLabel title="役職" />
                <Controller
                    name="job"
                    control={control}
                    render={({ field }) => (
                        <div>
                            <FormInput {...field} value={field.value ?? ''} hasError={!!errors.job?.message} />
                            {errors.job?.message && <FormError message={errors.job.message} />}
                        </div>
                    )}
                />

                <FormLabel title="部署" />
                <Controller
                    name="department"
                    control={control}
                    render={({ field }) => (
                        <div>
                            <FormInput {...field} value={field.value ?? ''} hasError={!!errors.department?.message} />
                            {errors.department?.message && <FormError message={errors.department.message} />}
                        </div>
                    )}
                />

                <ButtonContainer>
                    <ActionButton
                        disabled={loading}
                        onClick={submit}
                        bgColor="main-color-blue"
                        style={{
                            minWidth: '200px',
                            maxWidth: '400px',
                        }}
                    >
                        登録する
                    </ActionButton>
                </ButtonContainer>
            </Wrapper>
        </BgWrapper>
    );
};
